import {
  ExpandableSection,
  Flex,
  PremiumBreakdown,
  PremiumLineItem,
  PremiumSummary,
  smBumps,
  Typo,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useMemo } from 'react'

import { inForce, PriceOption, Quote } from '../../../domain'
import { useQuotePremiums } from '../../../hooks/premiums'
import {
  tFormats,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { ProductConfig } from '../../../state/configuration'
import { toDateTime } from '../../../util/date'
import { IPT } from '../product'
import {
  additionalLineItems,
  coreLineItems,
  discountLineItems,
  optionalLineItems,
} from './lineItems'

export interface StandardAdjustedPremiumMonthlyProps {
  currentQuote: Quote
  previousQuote?: Quote
  product: ProductConfig
  discount?: PriceOption
  expanded?: boolean
  tKey: TKeyBuilder
  onExpansionChange?: (isExpanded: boolean) => void
}

export const StandardAdjustedPremiumMonthly: FunctionComponent<
  StandardAdjustedPremiumMonthlyProps
> = ({
  currentQuote,
  previousQuote,
  product,
  discount,
  expanded,
  tKey,
  onExpansionChange,
}) => {
  const { t } = useWriskTranslation()

  const currentPremiums = useQuotePremiums(currentQuote, product, 'MONTHLY')
  const previousPremiums = useQuotePremiums(previousQuote, product, 'MONTHLY')

  const currentAmount = t(tFormats('currency.long'), {
    amount: currentPremiums.totals.discounted,
  })

  const yearlyTotalAmount = t(tFormats('currency.long'), {
    amount: currentPremiums.totals.yearly,
  })

  const yearlyTotal = t(tKey('monthly.yearly-total-amount'), {
    amount: yearlyTotalAmount,
  })

  const promo = inForce(discount)
    ? t(tKey('monthly.promotion'), {
        date: t(tFormats('date.long'), { value: toDateTime(discount?.endAt) }),
      })
    : undefined

  const subheader = [yearlyTotal, promo].filter(Boolean).join('\n')

  const header = (
    <PremiumSummary
      header={t<string>(tKey('monthly', 'header'))}
      subheader={subheader}
      currentAmount={currentAmount}
      previousAmount={
        previousQuote &&
        previousPremiums &&
        t<string>(tFormats('currency.long'), {
          amount: previousPremiums.totals.discounted,
        })
      }
    />
  )

  const lineItems = useMemo<PremiumLineItem[]>(
    () => [
      ...coreLineItems(t, tKey, currentPremiums, previousQuote && previousPremiums),
      ...discountLineItems(t, tKey, currentPremiums, previousQuote && previousPremiums),
      ...optionalLineItems(t, currentPremiums, previousQuote && previousPremiums),
    ],
    [currentPremiums, previousQuote, previousPremiums, t, tKey],
  )

  const additionalLineItemsMemo = useMemo(
    () => additionalLineItems(t, tKey, currentPremiums),
    [currentPremiums, t, tKey],
  )

  const extraInfo = (
    <Flex flexDirection='column' width={1} borderTopWidth={1} py={smBumps} gap={smBumps}>
      <Flex width={1}>
        <Typo typoSize='sm' fontWeight='bold'>
          {t(tKey('monthly.header'))}
        </Typo>
        <Typo typoSize='sm' fontWeight='bold'>
          {currentAmount}
        </Typo>
      </Flex>
      <Flex width={1}>
        <Typo typoSize='sm'>{t(tKey('monthly.yearly-total'))}</Typo>
        <Typo typoSize='sm'>{yearlyTotalAmount}</Typo>
      </Flex>
    </Flex>
  )

  return (
    <ExpandableSection
      header={header}
      open={expanded}
      reversed
      onChange={onExpansionChange}
    >
      <PremiumBreakdown
        extraInfo={extraInfo}
        lineItems={lineItems}
        additionalLineItems={additionalLineItemsMemo}
        ipt={<IPT amount={currentPremiums.totals.taxAmount} />}
        mx={smBumps}
      />
    </ExpandableSection>
  )
}
